import type { EffectRandom } from './random';

export const EXPLOSION_SPRITE_COUNT = 9;

export function rampAlpha(start: number, now: number, end: number, threshold: number): number {
  const peak = (end - start) * threshold + start;
  const span = now < peak ? peak - start : end - peak;
  if (span <= 0) return 0;
  return Math.max(0, Math.min(1, 1 - Math.abs(now - peak) / span));
}

const EXPLOSION_ALPHA_THRESHOLD = 0.2;

const SPIN_RANGE = 0.08;

export interface Explosion {
  readonly x: number;
  readonly y: number;
  readonly startAt: number;
  readonly endAt: number;
  readonly size: number;
  readonly sprite: number;
  angle: number;
  readonly spin: number;
}

export interface BurstShape {
  readonly count: [number, number];
  readonly startDelay: [number, number];
  readonly life: [number, number];
  readonly size: [number, number];
  readonly spread: number;
}

export const BURSTS = {
  player: { count: [7, 13], startDelay: [0, 250], life: [1000, 1500], size: [40, 55], spread: 40 },
  turret: { count: [4, 8], startDelay: [0, 100], life: [500, 900], size: [35, 45], spread: 55 },
  drone: { count: [2, 5], startDelay: [0, 100], life: [500, 900], size: [20, 35], spread: 20 },
  suicideBomb: {
    count: [4, 8],
    startDelay: [0, 100],
    life: [500, 900],
    size: [35, 50],
    spread: 30,
  },
  impact: { count: [1, 1], startDelay: [0, 0], life: [400, 400], size: [30, 30], spread: 0 },
} as const satisfies Record<string, BurstShape>;

export class ExplosionField {
  private readonly live: Explosion[] = [];

  get count(): number {
    return this.live.length;
  }

  get all(): readonly Explosion[] {
    return this.live;
  }

  burst(shape: BurstShape, x: number, y: number, now: number, random: EffectRandom): void {
    const count = random.int(shape.count[0], shape.count[1]);
    for (let i = 0; i < count; i++) {
      const [dx, dy] =
        shape.spread > 0
          ? [random.range(-shape.spread, shape.spread), random.range(-shape.spread, shape.spread)]
          : [0, 0];
      this.live.push({
        x: x + dx,
        y: y + dy,
        startAt: now + random.int(shape.startDelay[0], shape.startDelay[1]),
        endAt: now + random.int(shape.life[0], shape.life[1]),
        size: random.int(shape.size[0], shape.size[1]),
        sprite: random.int(0, EXPLOSION_SPRITE_COUNT - 1),
        angle: random.range(0, Math.PI * 2),
        spin: random.range(-SPIN_RANGE, SPIN_RANGE),
      });
    }
  }

  advance(now: number): void {
    let write = 0;
    for (const explosion of this.live) {
      if (now >= explosion.endAt) continue;
      explosion.angle += explosion.spin;
      this.live[write++] = explosion;
    }
    this.live.length = write;
  }

  alphaOf(explosion: Explosion, now: number): number {
    return rampAlpha(explosion.startAt, now, explosion.endAt, EXPLOSION_ALPHA_THRESHOLD);
  }

  clear(): void {
    this.live.length = 0;
  }
}
